import { useEffect, useState } from "react";
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Eye,
  Globe2,
  Loader2,
  LogOut,
  MapPin,
  Monitor,
  RefreshCw,
  Smartphone,
  Tablet,
  Timer,
  TrendingUp,
  Trash2,
  UploadCloud,
  CheckCircle2,
} from "lucide-react";
import { adminFetch, API_BASE, apiFile, formatApiErrorDetail } from "../lib/api";
import { CASE_STUDIES } from "../data/projects";

const CASE_IDS = ["chaparide", "embellish", "kytchn", "suited", "raison-home", "relish"];
const CASE_MAP = Object.fromEntries(CASE_STUDIES.map((p) => [p.id, p]));

function useNoIndex() {
  useEffect(() => {
    document.title = "Admin — Pix Marketing";
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex,nofollow");
  }, []);
}

function Login({ onDone }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(formatApiErrorDetail(body?.detail));
      }
      onDone({ username });
    } catch (e2) {
      setErr(e2.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-ink px-6 font-body text-white">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-panel p-8"
      >
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-vermilion">Pix® Admin</p>
        <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Studio login</h1>
        <label className="mt-6 block">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Username</span>
          <input
            data-testid="admin-login-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm outline-none transition-colors focus:border-vermilion"
          />
        </label>
        <label className="mt-4 block">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Password</span>
          <input
            data-testid="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm outline-none transition-colors focus:border-vermilion"
          />
        </label>
        {err && (
          <p data-testid="admin-login-error" className="mt-4 rounded-xl border border-vermilion/40 bg-vermilion/10 px-4 py-3 text-xs text-vermilion">
            {err}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-vermilion px-6 py-3.5 font-display text-sm font-bold uppercase tracking-tight text-ink transition-opacity disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Signing in" : "Sign in"}
        </button>
      </form>
    </main>
  );
}

function AdsTab({ onExpired }) {
  const [ads, setAds] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [msg, setMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const CHUNK = 512 * 1024;

  const load = async () => {
    const res = await adminFetch("/api/ads");
    if (res.status === 401) return onExpired();
    const raw = res.ok ? await res.json() : [];
    setAds(raw.map((a) => ({ id: a.id, title: a.title || "Studio ad", img: apiFile(a.storage_path) })));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const detail = async (res) => formatApiErrorDetail((await res.json().catch(() => null))?.detail);

  const upload = async () => {
    if (!file) { setMsg("Choose an image first — tap the file field, pick your ad, then press Upload."); return; }
    if (file.size > 10 * 1024 * 1024) { setMsg("That file is over 10MB — please resize it and try again."); return; }
    setBusy(true);
    setMsg("");
    setOkMsg("");
    setProgress(1);
    try {
      const ctype = file.type || "image/png";
      if (file.size > CHUNK) {
        const initRes = await adminFetch("/api/admin/ads/chunked/init", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filename: file.name, size: file.size, content_type: ctype, title }),
        });
        if (initRes.status === 401) return onExpired();
        if (!initRes.ok) throw new Error(await detail(initRes));
        const { sid, total } = await initRes.json();
        for (let i = 0; i < total; i++) {
          const slice = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
          const cRes = await adminFetch(`/api/admin/ads/chunked/${sid}/${i}`, { method: "POST", body: slice });
          if (cRes.status === 401) return onExpired();
          if (!cRes.ok) throw new Error(`Upload stalled at ${Math.round((i / total) * 100)}% — press Upload to retry`);
          setProgress(Math.round(((i + 1) / total) * 100));
        }
        const fin = await adminFetch(`/api/admin/ads/chunked/${sid}/finalize`, { method: "POST" });
        if (fin.status === 401) return onExpired();
        if (!fin.ok) throw new Error(await detail(fin));
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title);
        const res = await adminFetch("/api/admin/ads", { method: "POST", body: fd });
        if (res.status === 401) return onExpired();
        if (!res.ok) throw new Error(await detail(res));
      }
      setOkMsg("Ad uploaded — it's on the wall now.");
      setTitle("");
      setFile(null);
      const inp = document.querySelector("[data-testid=admin-ads-file-input]");
      if (inp) inp.value = "";
      await load();
    } catch (e) {
      setMsg(e.message || "Upload failed — please try again.");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this ad from the wall?")) return;
    const res = await adminFetch(`/api/admin/ads/${id}`, { method: "DELETE" });
    if (res.status === 401) return onExpired();
    await load();
  };

  return (
    <div data-testid="admin-ads-tab">
      <div className="rounded-2xl border border-white/10 bg-panel p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-tight">Upload a new ad</h2>
        <p className="mt-1 text-xs text-neutral-500">JPG, PNG, WebP, GIF or iPhone HEIC up to 10MB. It is appended to the end of the Brand in Focus wall.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="admin-ads-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ad title (optional)"
            className="flex-1 rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm outline-none transition-colors focus:border-vermilion"
          />
          <div className="flex-1">
            <input
              data-testid="admin-ads-file-input"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full rounded-xl border border-white/15 bg-ink px-4 py-2.5 text-xs text-neutral-400 file:mr-3 file:rounded-full file:border-0 file:bg-vermilion file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:text-ink"
            />
            {file && (
              <p className="mt-1.5 pl-1 font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                {file.name} · {(file.size / 1024 / 1024).toFixed(2)}MB
              </p>
            )}
          </div>
          <button
            data-testid="admin-ads-upload-btn"
            onClick={upload}
            disabled={busy}
            className="flex items-center justify-center gap-2 rounded-full bg-vermilion px-6 py-3 font-display text-xs font-bold uppercase tracking-tight text-ink transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {busy ? (progress ? `Uploading ${progress}%` : "Uploading") : "Upload"}
          </button>
        </div>
        {busy && (
          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-vermilion transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
        )}
        {okMsg && (
          <p data-testid="admin-ads-success" className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-400">
            <CheckCircle2 className="h-4 w-4" /> {okMsg}
          </p>
        )}
        {msg && <p data-testid="admin-ads-msg" className="mt-3 rounded-xl border border-vermilion/40 bg-vermilion/10 px-4 py-3 text-xs text-vermilion">{msg}</p>}
      </div>

      <h2 className="mt-8 mb-4 font-display text-sm font-bold uppercase tracking-tight text-neutral-400">
        Uploaded ads ({ads.length})
      </h2>
      {ads.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-xs text-neutral-600">
          No uploaded ads yet — the wall shows the built-in ads only.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {ads.map((a) => (
            <div key={a.id} className="overflow-hidden rounded-2xl border border-white/10 bg-panel">
              <img src={a.img} alt={a.title} className="aspect-[3/4] w-full object-cover" />
              <div className="flex items-center justify-between gap-2 p-3">
                <span className="truncate text-xs text-neutral-300">{a.title}</span>
                <button
                  data-testid={`admin-ad-delete-${a.id}`}
                  onClick={() => remove(a.id)}
                  aria-label={`Delete ${a.title}`}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 text-neutral-400 transition-colors hover:border-vermilion hover:bg-vermilion hover:text-ink"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CasesTab({ onExpired }) {
  const [caseId, setCaseId] = useState("chaparide");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [disciplines, setDisciplines] = useState("");
  const [posters, setPosters] = useState([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async (id) => {
    const res = await adminFetch("/api/cases");
    if (res.status === 401) return onExpired();
    const all = res.ok ? await res.json() : {};
    const project = CASE_MAP[id] || {};
    const ov = all[id] || {};
    setSubtitle(ov.subtitle ?? project.subtitle ?? "");
    setDescription(ov.description ?? project.description ?? "");
    const disc = ov.disciplines?.length ? ov.disciplines : project.services || [];
    setDisciplines(disc.join(", "));
    setPosters(ov.extra_posters || []);
    setMsg("");
    setSaved(false);
  };
  useEffect(() => { load(caseId); /* eslint-disable-next-line */ }, [caseId]);

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      const res = await adminFetch(`/api/admin/cases/${caseId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subtitle,
          description,
          disciplines: disciplines.split(",").map((s) => s.trim()).filter(Boolean),
        }),
      });
      if (!res.ok) throw new Error(formatApiErrorDetail((await res.json().catch(() => null))?.detail));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setMsg(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadPoster = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      const res = await adminFetch(`/api/admin/cases/${caseId}/posters`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(formatApiErrorDetail((await res.json().catch(() => null))?.detail));
      await load(caseId);
    } catch (err) {
      setMsg(err.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removePoster = async (pid) => {
    if (!window.confirm("Remove this poster from the case page?")) return;
    await adminFetch(`/api/admin/cases/${caseId}/posters/${pid}`, { method: "DELETE" });
    await load(caseId);
  };

  return (
    <div data-testid="admin-cases-tab">
      <div className="flex flex-wrap gap-2">
        {CASE_IDS.map((id) => (
          <button
            key={id}
            data-testid={`admin-case-pick-${id}`}
            onClick={() => setCaseId(id)}
            className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              caseId === id ? "border-vermilion bg-vermilion text-ink" : "border-white/15 text-neutral-400 hover:border-white/40"
            }`}
          >
            {id}
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-panel p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-tight">Case page text</h2>
        <label className="mt-4 block">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Subtitle</span>
          <input
            data-testid="admin-case-subtitle"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            className="w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm outline-none transition-colors focus:border-vermilion"
          />
        </label>
        <label className="mt-4 block">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Description</span>
          <textarea
            data-testid="admin-case-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-vermilion"
          />
        </label>
        <label className="mt-4 block">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Disciplines (comma separated)</span>
          <input
            data-testid="admin-case-disciplines"
            value={disciplines}
            onChange={(e) => setDisciplines(e.target.value)}
            className="w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm outline-none transition-colors focus:border-vermilion"
          />
        </label>
        <div className="mt-5 flex items-center gap-4">
          <button
            data-testid="admin-case-save-btn"
            onClick={save}
            disabled={busy}
            className="flex items-center gap-2 rounded-full bg-vermilion px-6 py-3 font-display text-xs font-bold uppercase tracking-tight text-ink transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <CheckCircle2 className="h-4 w-4" /> : null}
            {saved ? "Saved" : "Save changes"}
          </button>
          {msg && <p data-testid="admin-case-msg" className="text-xs text-vermilion">{msg}</p>}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-panel p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-tight">Extra posters</h2>
        <p className="mt-1 text-xs text-neutral-500">Added after the case's built-in gallery. The built-in posters cannot be deleted here.</p>
        <input
          data-testid="admin-case-poster-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={uploadPoster}
          disabled={busy}
          className="mt-4 rounded-xl border border-white/15 bg-ink px-4 py-2.5 text-xs text-neutral-400 file:mr-3 file:rounded-full file:border-0 file:bg-vermilion file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:text-ink"
        />
        {posters.length > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {posters.map((p) => (
              <div key={p.id} className="overflow-hidden rounded-xl border border-white/10">
                <img src={apiFile(p.storage_path)} alt={p.title} className="aspect-[3/4] w-full object-cover" />
                <button
                  data-testid={`admin-poster-delete-${p.id}`}
                  onClick={() => removePoster(p.id)}
                  className="flex w-full items-center justify-center gap-2 border-t border-white/10 py-2.5 font-mono text-[10px] uppercase tracking-widest text-neutral-400 transition-colors hover:bg-vermilion hover:text-ink"
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const SIZE_OPTIONS = [
  { id: "small", label: "Small" },
  { id: "normal", label: "Normal" },
  { id: "large", label: "Large" },
];

function LogosTab({ onExpired }) {
  const [logos, setLogos] = useState([]);
  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const res = await adminFetch("/api/logos");
    if (res.status === 401) return onExpired();
    setLogos(res.ok ? await res.json() : []);
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, fields) => {
    await adminFetch(`/api/admin/logos/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    });
    await load();
  };

  const move = async (idx, dir) => {
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= logos.length) return;
    const a = logos[idx];
    const b = logos[swapWith];
    await Promise.all([
      adminFetch(`/api/admin/logos/${a.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: swapWith + 1 }),
      }),
      adminFetch(`/api/admin/logos/${b.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: idx + 1 }),
      }),
    ]);
    await load();
  };

  const add = async () => {
    const file = document.querySelector("[data-testid=admin-logo-file-input]")?.files?.[0];
    if (!file) {
      setMsg("Choose a logo image first.");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", alt);
      const res = await adminFetch("/api/admin/logos", { method: "POST", body: fd });
      if (!res.ok) throw new Error(formatApiErrorDetail((await res.json().catch(() => null))?.detail));
      setAlt("");
      const inp = document.querySelector("[data-testid=admin-logo-file-input]");
      if (inp) inp.value = "";
      await load();
    } catch (e) {
      setMsg(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const replace = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const res = await adminFetch(`/api/admin/logos/${id}/replace`, { method: "POST", body: fd });
    if (!res.ok) setMsg(formatApiErrorDetail((await res.json().catch(() => null))?.detail));
    e.target.value = "";
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this logo from the strip?")) return;
    await adminFetch(`/api/admin/logos/${id}`, { method: "DELETE" });
    await load();
  };

  return (
    <div data-testid="admin-logos-tab">
      <div className="rounded-2xl border border-white/10 bg-panel p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-tight">Add a company logo</h2>
        <p className="mt-1 text-xs text-neutral-500">PNG/WebP works best (transparent background ideal). It joins the end of the strip.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="admin-logo-alt-input"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Company name"
            className="flex-1 rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm outline-none transition-colors focus:border-vermilion"
          />
          <input
            data-testid="admin-logo-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="flex-1 rounded-xl border border-white/15 bg-ink px-4 py-2.5 text-xs text-neutral-400 file:mr-3 file:rounded-full file:border-0 file:bg-vermilion file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:text-ink"
          />
          <button
            data-testid="admin-logo-add-btn"
            onClick={add}
            disabled={busy}
            className="flex items-center justify-center gap-2 rounded-full bg-vermilion px-6 py-3 font-display text-xs font-bold uppercase tracking-tight text-ink transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            Add
          </button>
        </div>
        {msg && <p data-testid="admin-logos-msg" className="mt-3 text-xs text-vermilion">{msg}</p>}
      </div>

      <h2 className="mt-8 mb-4 font-display text-sm font-bold uppercase tracking-tight text-neutral-400">
        Strip order ({logos.length})
      </h2>
      <div className="space-y-2">
        {logos.map((l, idx) => (
          <div key={l.id} data-testid={`admin-logo-row-${l.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-panel p-3">
            <span className="w-6 text-center font-mono text-[10px] text-neutral-600">{idx + 1}</span>
            <img
              data-testid={`admin-logo-img-${l.id}`}
              src={l.src.startsWith("/") ? l.src : apiFile(l.src)}
              alt={l.alt}
              className="h-8 w-16 object-contain"
              style={l.invert ? { filter: "invert(1)" } : l.lift ? { filter: "brightness(1.75)" } : undefined}
            />
            <input
              data-testid={`admin-logo-alt-${l.id}`}
              defaultValue={l.alt}
              onBlur={(e) => {
                if (e.target.value.trim() && e.target.value.trim() !== l.alt) patch(l.id, { alt: e.target.value.trim() });
              }}
              className="min-w-[120px] flex-1 rounded-lg border border-white/10 bg-ink px-3 py-2 text-xs outline-none transition-colors focus:border-vermilion"
            />
            <select
              data-testid={`admin-logo-size-${l.id}`}
              value={l.size}
              onChange={(e) => patch(l.id, { size: e.target.value })}
              className="rounded-lg border border-white/10 bg-ink px-2 py-2 text-xs text-white outline-none focus:border-vermilion"
            >
              {SIZE_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <button
              data-testid={`admin-logo-invert-${l.id}`}
              onClick={() => patch(l.id, { invert: !l.invert })}
              className={`rounded-full border px-3 py-2 font-mono text-[9px] uppercase tracking-widest transition-colors ${
                l.invert ? "border-vermilion bg-vermilion text-ink" : "border-white/15 text-neutral-400"
              }`}
            >
              White
            </button>
            <div className="flex items-center gap-1">
              <button data-testid={`admin-logo-up-${l.id}`} onClick={() => move(idx, -1)} aria-label="Move earlier" className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-neutral-400 transition-colors hover:border-white/50 hover:text-white">
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button data-testid={`admin-logo-down-${l.id}`} onClick={() => move(idx, 1)} aria-label="Move later" className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-neutral-400 transition-colors hover:border-white/50 hover:text-white">
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </div>
            <label className="cursor-pointer rounded-full border border-white/15 px-3 py-2 font-mono text-[9px] uppercase tracking-widest text-neutral-400 transition-colors hover:border-white/50 hover:text-white">
              Replace
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => replace(l.id, e)} />
            </label>
            <button
              data-testid={`admin-logo-delete-${l.id}`}
              onClick={() => remove(l.id)}
              aria-label={`Delete ${l.alt}`}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-neutral-400 transition-colors hover:border-vermilion hover:bg-vermilion hover:text-ink"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const fmtDur = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m ? `${m}m ${String(sec).padStart(2, "0")}s` : `${sec}s`;
};

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await adminFetch("/api/admin/analytics/summary");
        if (!res.ok) throw new Error("Could not load analytics");
        const d = await res.json();
        if (alive) {
          setData(d);
          setUpdated(new Date());
          setErr("");
        }
      } catch (e) {
        if (alive) setErr(e.message);
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (err) return <p data-testid="analytics-error" className="text-xs text-vermilion">{err}</p>;
  if (!data) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-neutral-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…
      </div>
    );
  }

  const t = data.totals;
  const maxDaily = Math.max(1, ...data.daily.map((d) => d.views));
  const deviceTotal = Math.max(1, data.devices.reduce((s, d) => s + d.count, 0));
  const devIcon = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

  return (
    <div data-testid="admin-analytics-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          Live · updates every 5s
        </p>
        <button
          data-testid="analytics-refresh-btn"
          onClick={() => setUpdated(null) || undefined}
          className="hidden"
        />
        <span data-testid="analytics-updated" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">
          {updated ? `Updated ${updated.toLocaleTimeString()}` : ""}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[
          { id: "analytics-live", label: "Live now", value: t.live_now, icon: Activity, accent: true },
          { id: "analytics-views", label: "Page views", value: t.pageviews, icon: Eye },
          { id: "analytics-visits", label: "Visits", value: t.visits, icon: TrendingUp },
          { id: "analytics-visitors", label: "Unique visitors", value: t.visitors, icon: Globe2 },
          { id: "analytics-duration", label: "Avg time on page", value: fmtDur(t.avg_duration_s), icon: Timer },
        ].map((c) => (
          <div key={c.id} data-testid={c.id} className={`rounded-2xl border p-4 ${c.accent ? "border-vermilion/50 bg-vermilion/5" : "border-white/10 bg-panel"}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">{c.label}</span>
              {c.accent ? (
                <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-vermilion">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-vermilion opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-vermilion" />
                  </span>
                  live
                </span>
              ) : (
                <c.icon className="h-3.5 w-3.5 text-neutral-600" />
              )}
            </div>
            <p className="mt-3 font-display text-3xl font-extrabold tracking-tight">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/10 bg-panel p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-tight">Last 14 days</h2>
        <div className="mt-6 flex h-36 items-end gap-2" data-testid="analytics-daily-chart">
          {data.daily.map((d) => (
            <div key={d.date} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex h-28 w-full items-end justify-center gap-1">
                <div
                  data-testid={`analytics-bar-views-${d.date}`}
                  className="w-2.5 rounded-t bg-vermilion transition-all"
                  style={{ height: `${(d.views / maxDaily) * 100}%`, minHeight: d.views ? 4 : 2, opacity: d.views ? 1 : 0.25 }}
                  title={`${d.views} views on ${d.date}`}
                />
                <div
                  className="w-2.5 rounded-t bg-white/40 transition-all"
                  style={{ height: `${(d.visits / maxDaily) * 100}%`, minHeight: d.visits ? 4 : 2, opacity: d.visits ? 1 : 0.25 }}
                  title={`${d.visits} visits on ${d.date}`}
                />
              </div>
              <span className="font-mono text-[8px] text-neutral-600">{d.date.slice(8)}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-4 font-mono text-[9px] uppercase tracking-widest text-neutral-600">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-vermilion" /> Page views</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-white/40" /> Visits</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-panel p-5">
          <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-tight">
            <MapPin className="h-4 w-4 text-vermilion" /> Where from — cities
          </h2>
          {data.cities.length === 0 ? (
            <p className="mt-4 text-xs text-neutral-600">No located visits yet.</p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="analytics-cities">
              {data.cities.map((c, i) => (
                <li key={`${c.city}-${c.country}-${i}`} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-300">
                    {c.city}
                    <span className="text-neutral-600"> · {c.country}</span>
                  </span>
                  <span className="font-mono text-xs text-vermilion">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
          <h2 className="mt-6 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-tight">
            <Globe2 className="h-4 w-4 text-vermilion" /> Countries
          </h2>
          <ul className="mt-4 space-y-2" data-testid="analytics-countries">
            {data.countries.map((c) => (
              <li key={c.country} className="flex items-center justify-between text-sm">
                <span className="text-neutral-300">{c.country}</span>
                <span className="font-mono text-xs text-vermilion">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-white/10 bg-panel p-5">
          <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-tight">
            <TrendingUp className="h-4 w-4 text-vermilion" /> How they got here
          </h2>
          <ul className="mt-4 space-y-2" data-testid="analytics-referrers">
            {data.referrers.map((r) => (
              <li key={r.source} className="flex items-center justify-between text-sm">
                <span className="text-neutral-300">{r.source}</span>
                <span className="font-mono text-xs text-vermilion">
                  {r.count} · {Math.round((r.count / Math.max(1, t.pageviews)) * 100)}%
                </span>
              </li>
            ))}
          </ul>

          <h2 className="mt-6 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-tight">
            <Monitor className="h-4 w-4 text-vermilion" /> Devices
          </h2>
          <div className="mt-4 space-y-3" data-testid="analytics-devices">
            {data.devices.map((d) => {
              const Icon = devIcon[d.device] || Monitor;
              return (
                <div key={d.device}>
                  <div className="flex items-center justify-between text-xs text-neutral-300">
                    <span className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-neutral-500" /> {d.device}
                    </span>
                    <span className="font-mono text-vermilion">
                      {d.count} · {Math.round((d.count / deviceTotal) * 100)}%
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-vermilion" style={{ width: `${(d.count / deviceTotal) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          <h2 className="mt-6 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-tight">
            <Eye className="h-4 w-4 text-vermilion" /> Top pages
          </h2>
          <ul className="mt-4 space-y-2" data-testid="analytics-pages">
            {data.pages.map((p) => (
              <li key={p.path} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-neutral-300">{p.path}</span>
                <span className="shrink-0 font-mono text-xs text-vermilion">
                  {p.views} · {fmtDur(p.avg_duration_s)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="text-[11px] leading-relaxed text-neutral-600">
        Counts begin on the first day shown in the chart — nothing before that was tracked, so early numbers are small by design.
        Cities and countries are approximate, based on visitor IP addresses.
      </p>
    </div>
  );
}

export default function AdminPage() {
  useNoIndex();
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("ads");

  useEffect(() => {
    adminFetch("/api/admin/me")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("no session"))))
      .then((u) => setUser(u))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await adminFetch("/api/admin/logout", { method: "POST" });
    setUser(false);
  };

  if (checking) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ink font-body text-neutral-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </main>
    );
  }
  if (!user) return <Login onDone={setUser} />;

  const onExpired = () => setUser(false);

  return (
    <main className="min-h-screen bg-ink px-6 py-10 font-body text-white lg:px-12">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-vermilion">Pix® Admin</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight">Studio panel</h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white transition-colors hover:border-vermilion hover:bg-vermilion hover:text-ink"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {[
            { id: "ads", label: "Single ads" },
            { id: "cases", label: "Case pages" },
            { id: "logos", label: "Client logos" },
            { id: "analytics", label: "Analytics" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                tab === t.id ? "border-vermilion bg-vermilion text-ink" : "border-white/15 text-neutral-400 hover:border-white/40"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "ads" ? <AdsTab onExpired={onExpired} /> : tab === "cases" ? <CasesTab onExpired={onExpired} /> : tab === "logos" ? <LogosTab onExpired={onExpired} /> : <AnalyticsTab />}
        </div>
      </div>
    </main>
  );
}